// ── Bulk import page ("/cohorts/:cohort_id/participants/bulk") — Wave 5 ──────
// The dedicated bulk-import screen for ONE cohort. Reads the cohort header (for
// the Week-3 cutoff start date + the cohort label) and the cohort roster (for the
// active-email duplicate guard), routed through useHonestQuery -> <HonestState>,
// then opens the BulkImportModal. The page OWNS the bulkImport mutation
// (idempotency-key + cache invalidation via the existing useCohortLifecycleMutations
// hook) and toasts ONLY after a committed success.
//
// ── ROLE + SCOPE + CUTOFF GATES (FR-PART-002, blueprint §6 gate #4) ─────────
//   • participants:bulk-import capability → SA / Admin / Coach allowed; everyone
//     else (company_admin / cohort_lead / participant) is DENIED (read-only/hidden).
//   • The participants module guard (canAccessParticipantsModule) fail-closes a
//     participant before any read fires.
//   • COACH Week-3 cutoff (resolveCoachCutoff): SA/Admin never gated; a coach past
//     week 3 sees a gate-closed posture and the import affordance is disabled.
//     The API independently enforces the cutoff with 410 CO_CUTOFF_EXCEEDED, and
//     the assigned-cohort mutation scope remains server-authoritative.

import { Button, PageFrame, useToast } from "@vjal/ui";
import { useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import { useCohortLifecycleMutations } from "../../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../../domain/capabilities";
import type {
  CoachingCohortDto,
  CoachingOperationResult,
  CoachingParticipantDto,
  ListResponse,
} from "../../../services/coaching-dtos";
import type { ProductScreenContext } from "../../product-route-contracts";
import { liveRecordIdFromPath } from "../../product-live-rows";
import { canAccessParticipantsModule, isValidParticipantId } from "../participants-helpers";
import { BulkImportModal } from "./bulk-import-modal";
import { resolveCoachCutoff } from "./coach-cutoff";

const services = getCoachingServices();

export function BulkImportPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useCohortLifecycleMutations();

  const cohortId = liveRecordIdFromPath(routePath, "cohorts");
  const validId = isValidParticipantId(cohortId);

  // Capability + module gates (fail-closed). Import is SA/Admin/Coach only.
  const importDecision = evaluateActionCapability(role, "participants:bulk-import");
  const moduleDenied = !canAccessParticipantsModule(role);
  const denied = moduleDenied || !importDecision.allowed;

  const [open, setOpen] = useState(false);

  // Cohort header read (start date + label for the cutoff + modal title).
  const cohortQuery = useHonestQuery<CoachingCohortDto>(
    coachingQueryKeys.cohortDetail(cohortId ?? ""),
    () => services.cohorts.detail(cohortId as string),
    { enabled: validId && !denied },
  );
  const cohort = cohortQuery.data ?? null;

  // Cohort roster read — the active-email set powers the client-side duplicate
  // guard in the preview (defense-in-depth; the server re-checks).
  const rosterQuery = useHonestQuery<ListResponse<CoachingParticipantDto>>(
    coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId ?? "" }),
    () => services.participants.listForCohort(cohortId as string),
    { enabled: validId && !denied },
  );
  const existingEmails = useMemo(
    () =>
      (rosterQuery.data?.items ?? [])
        .map((participant) => participant.email?.trim().toLowerCase())
        .filter((email): email is string => Boolean(email)),
    [rosterQuery.data],
  );

  const cutoff = useMemo(() => resolveCoachCutoff(role, cohort?.start_date ?? null), [role, cohort?.start_date]);

  // ── Fail closed: a denied role gets permission-denied, NO read, NO affordance.
  if (denied) {
    return (
      <section aria-label="Bulk import participants" className="col-span-12" data-testid="bulk-import-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You can't import participants"
            message="Bulk import is for coaching staff. Ask a super admin if you need participants added."
          />
        </div>
      </section>
    );
  }

  // ── Invalid/missing cohort id → empty (never a blank). ──────────────────────
  if (!validId) {
    return (
      <section aria-label="Bulk import participants" className="col-span-12" data-testid="bulk-import-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState posture="empty" title="No cohort selected" message="Open bulk import from a cohort to choose who to add." />
        </div>
      </section>
    );
  }

  // The cohort header read drives the page posture (loading/unavailable/empty/…).
  const headerPosture = cohortQuery.posture;
  const cohortLabel = cohort?.name ?? "this cohort";

  async function submitImport(rows: Array<Record<string, unknown>>): Promise<CoachingOperationResult> {
    // dry_run:false is an explicit COMMIT. The live backend ignores dry_run (it
    // always commits — there is no server preview); the mock adapter uses it to
    // distinguish preview vs commit. Our preview is client-side, so this POST is
    // always the real commit.
    const operation = await mutations.bulkImport.mutateAsync({
      cohortId: cohortId as string,
      body: { rows, dry_run: false },
    });
    // Honest: toast ONLY on a committed success (ok:true). A server-reported
    // validation/partial failure resolves with ok:false (mock) and is rendered by
    // the modal as the authoritative result — NO success toast here. A thrown
    // CO_VALIDATION_FAILED never reaches this line (the modal catches it).
    if (operation.ok) {
      toast.success("Participants imported.");
    }
    return operation;
  }

  return (
    // The route shell owns the page h1 in every state (including the early returns above).
    <PageFrame
      titleAs={2}
      title="Bulk import participants"
      subtitle={
        headerPosture === null
          ? `Add many participants to ${cohortLabel} from a CSV.`
          : "Add many participants to a cohort from a CSV."
      }
      className="col-span-12"
    >
      <section aria-label="Bulk import participants" className="flex flex-col gap-4" data-testid="bulk-import-page">
        <div className="rounded-xl border border-line bg-white p-5">
          {headerPosture !== null ? (
            <HonestState
              posture={headerPosture}
              gate={cohortQuery.gate}
              reference={cohortQuery.reference}
              onRetry={cohortQuery.retry}
              title={headerPosture === "empty" ? "Cohort not found" : undefined}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {cutoff.state === "closed" ? (
                // Coach past Week 3: the affordance is disabled with the honest gate.
                <HonestState
                  posture="gate-closed"
                  gate={{ closed: true, reason: cutoff.reason }}
                  title="The add window for this cohort has closed"
                />
              ) : (
                <div className="flex items-center gap-3">
                  <Button variant="primary" onClick={() => setOpen(true)} data-testid="open-bulk-import">
                    Import from CSV
                  </Button>
                  {cutoff.currentWeek != null && (
                    <span className="text-sm text-ink-muted">Cohort week {cutoff.currentWeek}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {headerPosture === null && cutoff.state !== "closed" && (
          <BulkImportModal
            open={open}
            cohortLabel={cohortLabel}
            existingEmails={existingEmails}
            cutoff={cutoff}
            onClose={() => setOpen(false)}
            onSubmit={submitImport}
            submitting={mutations.bulkImport.isPending}
          />
        )}
      </section>
    </PageFrame>
  );
}
